import { ARTIFACT_HIT_TESTING_LAW } from '~/design-system/components/DeckGen/artifactAgent/guriTheme';
import type { BrandKit } from '~/design-system/components/DeckGen/birtvy/brandKit';
import { resolveActiveBrandKit } from '~/design-system/components/DeckGen/birtvy/brandKit';
import {
  resolveKitForGeneration,
  type BrandKitBinding,
} from '~/design-system/components/DeckGen/birtvy/brandKitDefault';
import {
  brandKitFromBinding,
  resolveArtifactStyle,
} from '~/design-system/components/DeckGen/style/styleForArtifact';
import type { DeckBlob } from '../deckContent';
import { stripHandMarks } from '~/design-system/components/DeckGen/birtvyOpen/artifactHand';

export const PRODUCT_DEFAULT_THEME =
  'ONE LOOK. Commit to a single readable aesthetic for the whole document. Prefer clear type, real contrast, and whitespace over decoration. No glow, no glassmorphism, no gradient mesh for its own sake.';

const SPINE_MAX = 20_000;

export function kitFromBlob(blob: DeckBlob): BrandKit | null {
  const kits = asKits(blob.designSystems);
  const activeId = typeof blob.activeDesignSystemId === 'string' ? blob.activeDesignSystemId : null;
  return resolveActiveBrandKit(kits, activeId) ?? brandKitFromBinding(bindingFromBlob(blob));
}

/** The blob's stored binding, or null. The one field `DeckContent.brandKitBinding` surfaces. */
export function bindingFromBlob(blob: DeckBlob): BrandKitBinding | null {
  return isBinding(blob.brandKitBinding) ? blob.brandKitBinding : null;
}

/**
 * /deck's seat at the one resolver (`resolveKitForGeneration`): the blob's kit read LIVE, the blob's
 * binding as the fallback and refreshed on every landing, the user's DEFAULT kit last.
 *
 * `blobKit` is what `kitFromBlob` already settled — the active design system, else the kit the binding
 * describes — so it goes in as the active tier and the binding rides beside it for the fallback. The
 * two name the same kit on every blob the Design menu has written (`chooseKitIn` moves both together).
 *
 * `pendingBinding` is what `withKitBinding` stamps once the document lands: the REFRESHED snapshot after
 * a live read, null when nothing changed (a failed read hands the deck's own binding back, and rewriting
 * it byte for byte would be a write for nothing). `optOut` is the deck's explicit "no kit, the deck's
 * own look" choice — it silences every tier, the default one included, or the choice cannot be made by
 * a user who has a default kit.
 */
export async function resolveWorkspaceKit(
  blobKit: BrandKit | null,
  optOut?: boolean,
  binding?: BrandKitBinding | null,
): Promise<{
  kit: BrandKit | null;
  pendingBinding: BrandKitBinding | null;
}> {
  const resolved = await resolveKitForGeneration({ activeKit: blobKit, binding, optOut });
  const pendingBinding = resolved.binding && resolved.binding !== binding ? resolved.binding : null;
  return { kit: resolved.kit, pendingBinding };
}

export function withKitBinding(blob: DeckBlob, pending: BrandKitBinding | null): DeckBlob {
  if (!pending) return blob;
  const existing = isBinding(blob.brandKitBinding) ? blob.brandKitBinding : null;
  if (existing && existing.kitId !== pending.kitId) return blob;
  return { ...blob, brandKitBinding: pending };
}

/**
 * The user PICKED a kit (the Design menu). Unlike the generation-side `withKitBinding` stamp this
 * overwrites any prior binding, clears the opt-out, and moves `activeDesignSystemId` — which
 * outranks the binding in `kitFromBlob`, so leaving a stale one would make the old kit keep winning.
 */
export function chooseKitIn(blob: DeckBlob, binding: BrandKitBinding): DeckBlob {
  const inDesignSystems = asKits(blob.designSystems).some((kit) => kit.id === binding.kitId);
  return {
    ...blob,
    brandKitBinding: binding,
    kitOptOut: false,
    activeDesignSystemId: inDesignSystems ? binding.kitId : null,
  };
}

/** The user picked "Deck theme": no kit, not even the account default. The binding stays stored so
 *  re-picking the kit later is one click; `kitOptOut` is what every reader honors. */
export function optOutOfKitIn(blob: DeckBlob): DeckBlob {
  return { ...blob, kitOptOut: true };
}

export function workspaceThemeRecipe(input: {
  brandKit: BrandKit | null;
  docTheme: string;
}): { recipe: string; hasKit: boolean } {
  const hasKit = input.brandKit != null;
  const docTheme = input.docTheme.trim();
  /* ONE BUDGET. The recipe is fitted to MAX_THEME_RECIPE minus what this function appends after it, so
     the route's flat slice at 8,000 never bites. Measured on the Deck AI kit on 2026-09-05 before this:
     8,432 chars, the last 432 of the LOGO law cut mid-sentence. Only the hit-testing law rides here, the
     same choice `kitThemeRecipe` makes: the LOGOS paragraph restates the contract's own LOGOS section,
     which is already in the system prompt, and on a full kit it cost 1,000 chars to say it twice. */
  const resolved = resolveArtifactStyle(
    { brandKit: input.brandKit, docTheme },
    { reserve: ARTIFACT_HIT_TESTING_LAW.length + 2 },
  ).recipe.trim();
  const core = hasKit ? resolved || PRODUCT_DEFAULT_THEME : docTheme || PRODUCT_DEFAULT_THEME;
  return { recipe: `${core}\n\n${ARTIFACT_HIT_TESTING_LAW}`, hasKit };
}

export function artifactGist(html: string): string {
  const text = html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.slice(0, 110);
}

export function standingLookPrompt(
  siblings: { title: string; html: string; at: number }[],
  includeSpine: boolean,
): string {
  if (!siblings.length) return '';
  const lines = siblings.map(
    (s) => `- "${s.title || 'untitled'}": ${artifactGist(s.html)}`,
  );
  let spine = '';
  if (includeSpine) {
    const newest = siblings.reduce((best, s) => (s.at > best.at ? s : best));
    // A reference is not the user's document: its hand marks would ride into the new artifact as
    // copies (ARTIFACT-LAYOUT-EDITING.md section 2), so they come off before it is shown to the writer.
    const look = newest.html ? stripHandMarks(newest.html) : '';
    if (look && look.length <= SPINE_MAX) {
      spine = `\nTHE STANDING LOOK — the newest artifact of this set. Match its fonts, palette, spacing rhythm and type scale exactly; only the content differs:\n<standing-look>\n${look}\n</standing-look>`;
    }
  }
  return `\n\nTHREAD CONTEXT. This artifact joins a connected set on one page.\nThe set so far:\n${lines.join('\n')}\nKeep ONE visual language across the set, and never duplicate content a sibling already covers.${spine}`;
}

function asKits(value: unknown): BrandKit[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is BrandKit =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as BrandKit).id === 'string' &&
      !!(item as BrandKit).profile,
  );
}

export function isBinding(value: unknown): value is BrandKitBinding {
  if (!value || typeof value !== 'object') return false;
  const binding = value as BrandKitBinding;
  return typeof binding.kitId === 'string' && !!binding.profile;
}
